import tw, { css, styled } from "twin.macro"
import Image from "next/image"
import ReactMarkdown from "react-markdown"
import Zoom from "react-medium-image-zoom"
import { Attachment } from "airtable/lib/attachment"

interface FeaturedImageProps {
  image_preview: Array<Attachment>
  image_url: string
  caption: string
}
const FeaturedImage = ({ image_url, image_preview, caption }: FeaturedImageProps) => {
  const preview = image_preview[0].thumbnails?.large
  const width = preview && preview.width
  const height = preview && preview.height

  const altText = caption ? `${caption} – The Richard Pousette-Dart Foundation` : `The Richard Pousette-Dart Foundation`

  return (
    <Zoom
      zoomImg={{
        alt: altText,
        src: image_url,
        srcSet: image_url,
      }}
    >
      <Image
        key={image_preview[0].id}
        src={image_url}
        width={width}
        height={height}
        alt={altText}
        sizes="(max-width: 768px) 100vw, 33vw"
      />
    </Zoom>
  )
}

const StyledText = styled("div")(() => [
  tw`space-y-4`,
  css`
    p strong {
      ${tw`
        font-black
      `}
    }
    a {
      ${tw`text-link underline`}
      ${css`
        text-underline-offset: 3px;
        text-decoration-style: dotted;
      `}
    }
    code {
      ${tw`font-serif font-bold text-rpd-red`}
    }
  `,
])

const CardCaption = styled("div")(() => [
  tw`mt-8 font-sans font-normal text-xs text-rpd-gray-9 leading-snug tablet-lg:(mt-10)`,
  css`
    p {
      margin-bottom: 0;
      ${tw`font-sans font-normal text-xs leading-snug`}
    }
    a {
      ${tw`text-link`}
      ${css`
        text-decoration-line: underline;
        text-decoration-style: dotted;
        text-underline-offset: 3px;
      `}
    }
  `,
])

interface CardProps {
  deck: string
  body: string
  image_url: string
  image_preview: Array<Attachment>
  caption: string
}

export default function Card(props: CardProps) {
  const { deck, body, image_url, image_preview, caption } = props

  return (
    <div className="card-embed" tw="my-10 bg-rpd-gray-2 px-6 py-8 bg-opacity-20 shadow-md">
      <div tw="grid grid-cols-4 tablet-lg:(grid-cols-12) gap-4 tablet-lg:(gap-6 gap-y-4)">
        <div tw="col-span-4 tablet-lg:(col-span-7)">
          <div tw="mr-4">
            <StyledText>
              <ReactMarkdown>{deck}</ReactMarkdown>
              {body && (
                <div tw="text-sm">
                  <ReactMarkdown>{body}</ReactMarkdown>
                </div>
              )}
            </StyledText>
          </div>
        </div>

        {image_url && (
          <div tw="col-span-4 row-span-2 tablet-lg:(col-start-8 col-span-5)">
            <FeaturedImage image_url={image_url} image_preview={image_preview} caption={caption} />
          </div>
        )}
        {caption && (
          <div tw="col-span-4 tablet-lg:(col-span-7 row-start-2)">
            <div tw="flex flex-col justify-end h-full">
              <CardCaption>
                <ReactMarkdown>{caption}</ReactMarkdown>
              </CardCaption>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
